import { plainToClass } from "class-transformer";
import { LineStringFeature } from "../../../../../utils/models/LineStringFeature";
import type { UndoCommand, CommandContext } from "../types";

interface ReversibleRoute {
  markers: any[];
  lineStrings: any[];
  updated_at?: Date;
}

/** Reverse a route in place: marker order, segment order, and each segment's own geometry and turns. */
export function reverseRouteInPlace(route: ReversibleRoute): void {
  route.markers.reverse();
  route.lineStrings = route.lineStrings.map(lineString => {
    const instance = plainToClass(LineStringFeature, lineString);
    return instance.reverse();
  }).reverse();
  route.updated_at = new Date();
}

/**
 * Command for reversing a route.
 *
 * Not self-inverse: reversing mirrors each segment's turns, and mirroring drops what cannot be
 * inverted — GraphHopper's wording, cues, a person's re-worded text. Reversing twice therefore
 * does not restore the original, so undo restores a snapshot taken before the reverse. Redo
 * restores one taken after it, because bike and car segments were routed again and reversing
 * once more would mirror them instead. Both are deep copies so later edits cannot reach them.
 */
export class ReverseRouteCommand implements UndoCommand {
  readonly type = 'REVERSE_ROUTE';
  readonly routeKey: string;

  private readonly markersBefore: any[];
  private readonly lineStringsBefore: any[];
  private readonly markersAfter: any[];
  private readonly lineStringsAfter: any[];

  constructor(routeKey: string, routeBefore: ReversibleRoute, routeAfter: ReversibleRoute) {
    this.routeKey = routeKey;
    this.markersBefore = JSON.parse(JSON.stringify(routeBefore.markers));
    this.lineStringsBefore = JSON.parse(JSON.stringify(routeBefore.lineStrings));
    this.markersAfter = JSON.parse(JSON.stringify(routeAfter.markers));
    this.lineStringsAfter = JSON.parse(JSON.stringify(routeAfter.lineStrings));
  }

  undo(ctx: CommandContext): void {
    const route = ctx.state.routes[this.routeKey];
    if (!route) return;

    route.markers = JSON.parse(JSON.stringify(this.markersBefore));
    route.lineStrings = JSON.parse(JSON.stringify(this.lineStringsBefore));
    route.updated_at = new Date();
  }

  redo(ctx: CommandContext): void {
    const route = ctx.state.routes[this.routeKey];
    if (!route) return;

    route.markers = JSON.parse(JSON.stringify(this.markersAfter));
    route.lineStrings = JSON.parse(JSON.stringify(this.lineStringsAfter));
    route.updated_at = new Date();
  }
}
